import {
	Stack,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import { getTranslationStatus } from 'components/cattles/lamenessPage/shared/processes'
import { morphForWords } from 'components/shared/processes'
import { stripParenthesesContent } from 'components/shared/processes/strings/stripContent'
import { IHoofHealthTable } from 'models/ICattlesData'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'
import { COLORS } from 'styles/colors'
import Preloader from 'ui/Preloader'
import CustomTableRow from 'ui/table/CustomTableRow'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'

interface RoofHealthTableProps {
	cowId: number
}
const HoofHealthTable = (props: RoofHealthTableProps) => {
	const { cowId } = props
	const [healthInfo, setHealthInfo] = useState<IHoofHealthTable[]>([])
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const transformForTableData = useCallback(
		(data: IHoofHealthTable[]) => {
			return data?.map((el, index) => ({
				rowKey: `keyId=${index}`,
				height: '100%',
				cellItems: [
					{
						padding: '16px 8px 0px 0px',
						children: (
							<TypographyPrimary>
								{moment(el.date).format('DD.MM.YY')}
							</TypographyPrimary>
						),
					},
					{
						padding: '16px 8px 0px 0px',
						children: (
							<>
								{el.limpness ? (
									<TypographyPrimary key={`limpness-${index}`}>
										{el.limpness}{' '}
										<span style={{ color: COLORS.secondaryFont }}>
											{morphForWords(el.limpness, ['балл', 'балла', 'баллов'])}
										</span>
									</TypographyPrimary>
								) : (
									<TypographySecondary14>{'-'}</TypographySecondary14>
								)}
							</>
						),
					},
					{
						padding: '16px 8px 0px 0px',
						children: (
							<TypographyPrimary
								sx={{
									color: el.event === 'Выбытие' ? COLORS.error : COLORS.black,
								}}
							>
								{el.event}
							</TypographyPrimary>
						),
					},
					{
						padding: '16px 8px 0px 0px',
						children: el.diagnosis ? (
							<Stack
								display={'flex'}
								flexDirection={'column'}
								key={`diagnoses-${index}`}
								gap={'12px'}
							>
								{el.diagnosis.map((diagnose, diagIndex) => (
									<React.Fragment key={`diagnose-${index}-${diagIndex}`}>
										<TypographyPrimary>
											{diagnose.sickness
												.map(sicknes => stripParenthesesContent(sicknes.title))
												.join(', ')}
										</TypographyPrimary>
										<TypographySecondary>
											{getTranslationStatus(diagnose.position)}
										</TypographySecondary>
									</React.Fragment>
								))}
							</Stack>
						) : (
							<TypographySecondary14>{'-'}</TypographySecondary14>
						),
					},
					{
						padding: '16px 8px 0px 0px',
						children: el.treatment_plan ? (
							<TypographyPrimary>{el.treatment_plan}</TypographyPrimary>
						) : (
							<TypographySecondary14>{'-'}</TypographySecondary14>
						),
					},
					{
						padding: '16px 8px 0px 0px',
						children: el.treatment_duration ? (
							<TypographySecondary>
								Продолжительность: {el.treatment_duration}{' '}
								{morphForWords(el.treatment_duration, ['день', 'дня', 'дней'])}
							</TypographySecondary>
						) : (
							<TypographySecondary14>{'-'}</TypographySecondary14>
						),
					},
					{
						padding: '16px 8px 0px 0px',
						children: el.examiner ? (
							<TypographyPrimary>{el.examiner}</TypographyPrimary>
						) : (
							<TypographySecondary14>{'-'}</TypographySecondary14>
						),
					},
				],
			}))
		},
		[cowId],
	)

	useEffect(() => {
		setIsLoading(true)
		CattlesApiService.getHoofHealthTableByCowId(cowId)
			.then(res => {
				setHealthInfo(res.reverse())
			})
			.catch(err => console.error(err))
			.finally(() => setIsLoading(false))
	}, [cowId])

	const transformedData = transformForTableData(healthInfo)

	return (
		<>
			<TableContainer
				sx={{
					marginTop: '24px',
					overflowY: 'auto',
					overflowX: 'hidden',
					height: '267px',
					'&::-webkit-scrollbar': {
						width: 4,
					},
					'&::-webkit-scrollbar-track': {
						backgroundColor: '#DBDBDB',
						borderRadius: 12,
					},
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: COLORS.icon,
						borderRadius: 12,
					},
				}}
			>
				<Table>
					<TableHead
						sx={{
							[`& .${tableCellClasses.root}`]: {
								backgroundColor: '#fff',
							},
						}}
					>
						<CustomTableRow
							rowKey={tableHead.rowKey}
							cellItems={tableHead.cellItems}
							rowBorderBottom={tableHead.rowBorderBottom}
							rowHeight={tableHead.height}
						/>
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow
								sx={{
									width: '100%',
									[`& .${tableCellClasses.root}`]: {
										borderBottom: 'none',
										height: '200px',
									},
								}}
							>
								<TableCell colSpan={7} align={'center'} padding={'none'}>
									<Preloader color={COLORS.secondaryFont} />
								</TableCell>
							</TableRow>
						) : !transformedData || transformedData.length === 0 ? (
							<TableRow
								sx={{
									width: '100%',
									[`& .${tableCellClasses.root}`]: {
										borderBottom: 'none',
										height: '200px',
									},
								}}
							>
								<TableCell colSpan={7} align={'center'} padding={'none'}>
									<Stack
										height='200px'
										direction='column'
										justifyContent='center'
										alignItems='center'
										gap='8px'
									>
										<TypographySecondary14
											sx={{ color: '#7F7F84', fontWeight: 200 }}
										>
											Признаков хромоты не обнаружено.
										</TypographySecondary14>
									</Stack>
								</TableCell>
							</TableRow>
						) : (
							transformedData.map(el => (
								<CustomTableRow
									key={el.rowKey}
									rowKey={el.rowKey}
									cellItems={el.cellItems}
									rowHeight={el.height}
								/>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{healthInfo.length > 0 && (
				<TypographySecondary sx={{ marginTop: '24px' }}>
					{'Всего: ' + healthInfo.length}
				</TypographySecondary>
			)}
		</>
	)
}

export default HoofHealthTable

const tableHead = {
	rowKey: 'tableHistoryViewHead',
	rowBorderBottom: COLORS.secondaryFontOpacity,
	height: '40px',
	cellItems: [
		{
			width: '7%',
			children: <TypographySecondary>Дата</TypographySecondary>,
		},
		{
			width: '12%',
			children: (
				<TypographySecondary>
					Балл
					<br />
					хромоты
				</TypographySecondary>
			),
		},
		{
			width: '12%',
			children: <TypographySecondary>Событие</TypographySecondary>,
		},
		{
			width: '18%',
			children: (
				<TypographySecondary>
					Диагноз
					<br />
					больное копыто
				</TypographySecondary>
			),
		},
		{
			width: '15%',
			children: <TypographySecondary>Схема лечения</TypographySecondary>,
		},
		{
			width: '15%',
			children: (
				<TypographySecondary>
					Продолжительность
					<br />
					схемы лечения
				</TypographySecondary>
			),
		},
		{
			width: '12%',
			children: <TypographySecondary>Исполнитель</TypographySecondary>,
		},
	],
}
